import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Logo } from '@/components/brand';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/lib/auth';

interface SidebarProps {
  activeItem?: string;
  user?: any;
  /**
   * Page-level override for canvas tools (e.g. Dwarsdoorsnede): start in this
   * state and leave the saved preference ('sidebar-collapsed') untouched.
   */
  collapsed?: boolean;
}

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /**
   * When true, clicking triggers a full-page navigation (window.location.href)
   * instead of react-router navigate(). Required when the path is served by a
   * different app on the same host (e.g. /viewer → sb-viewer SvelteKit).
   */
  external?: boolean;
}

interface NavigationSection {
  id: string;
  title: string;
  items: NavigationItem[];
}

export function Sidebar({ activeItem = 'dashboard', user, collapsed }: SidebarProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(collapsed ?? false);

  // Load collapsed state from localStorage on mount (unless the page overrides it)
  useEffect(() => {
    if (collapsed !== undefined) return;
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      setDesktopCollapsed(saved === 'true');
    }
  }, [collapsed]);

  // Save collapsed state to localStorage when it changes
  const toggleDesktopCollapsed = () => {
    setDesktopCollapsed(prev => {
      const newValue = !prev;
      if (collapsed === undefined) localStorage.setItem('sidebar-collapsed', String(newValue));
      return newValue;
    });
  };

  const navigationSections: NavigationSection[] = [
    {
      id: 'main',
      title: 'Start',
      items: [
        { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      ],
    },
    {
      id: 'tools-services',
      title: 'Tools & diensten',
      items: [
        { id: 'tools', label: 'Zelfservice tools', path: '/tools', icon: Wrench },
        // Kaarten & Bibliotheek = landing page in this SPA (pages/MapsLibrary.tsx) with three cards:
        // Kaart (GeoLibre, /map), Bibliotheek (STAC catalog, data.studiobereikbaar.nl), Thematische
        // viewers (sb-viewer, /viewer). Not /maps: the /map Traefik router swallows that prefix.
        { id: 'viewers', label: 'Kaarten & Bibliotheek', path: '/kaarten', icon: MapIcon },
        // Projecten = pages/Projecten.tsx: the intake app's project list and form (sb-facturatie-intake,
        // sb-finance repo, /intake/*) framed under this sidebar. /alle-projecten, not /projecten: the
        // Streamlit /project router (sb-projects-frontend, unlinked) would catch /projecten.
        { id: 'projects', label: 'Projecten', path: '/alle-projecten', icon: Briefcase },
        { id: 'services', label: 'Datateam services', path: '/services', icon: Users },
        // InnoFlashes and Documentation left the menu 2026-10-14 (Gio). Routes /innoflashes and
        // /documentation/* stay live: Data Team Services cards still deep-link to /innoflashes.
      ],
    },
  ];

  const handleNavigation = (item: NavigationItem) => {
    if (item.external) {
      // Full-page nav so a different app on the same host can take over routing
      // (e.g. /viewer is served by sb-viewer SvelteKit, not by this React app).
      window.location.href = item.path;
      return;
    }
    navigate(item.path);
    setMobileOpen(false);
  };

  // Sidebar content component (reused for both desktop and mobile)
  const SidebarContent = ({ isCollapsed = false }: { isCollapsed?: boolean }) => {
    return (
      <>
      {/* Logo */}
      <div className={isCollapsed ? 'p-4 flex justify-center' : 'px-6 pt-6 pb-4'}>
        <Logo
          variant="black"
          size={isCollapsed ? 'xs' : 'md'}
          className={isCollapsed ? 'w-10' : 'w-full'}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3">
        {navigationSections.map((section, sectionIndex) => (
          <div key={section.id} className={sectionIndex > 0 ? 'mt-6' : ''}>
            {!isCollapsed && (
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                {section.title}
              </p>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = item.id === activeItem;
                return (
                  <li key={item.id}>
                    <button
                      className={`group relative w-full flex items-center gap-3 rounded-xl border px-3 py-2 text-sm font-medium transition-all ${
                        isCollapsed ? 'justify-center' : 'justify-start'
                      } ${
                        isActive
                          ? 'bg-[#FCEDEE] text-[#E62E73] border-[#F3B2BE] shadow-sm'
                          : 'border-transparent text-gray-600 hover:bg-[#FCEDEE] hover:text-[#E62E73]'
                      }`}
                      onClick={() => handleNavigation(item)}
                      title={isCollapsed ? item.label : undefined}
                      aria-label={isCollapsed ? item.label : undefined}
                    >
                      {!isCollapsed && (
                        <span
                          className={`h-8 w-1 rounded-full transition-colors ${
                            isActive
                              ? 'bg-[#E62E73]'
                              : 'bg-transparent group-hover:bg-[#F3B2BE]'
                          }`}
                        />
                      )}
                      <Icon size={18} />
                      {!isCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User section at bottom */}
      {user && (
        <div className="border-t border-gray-200 p-4">
          {!isCollapsed && (
            <div className="mb-2">
              <p className="text-sm font-semibold text-[#164E4B] truncate">
                {user.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={logout}
            className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 transition-all ${
              isCollapsed ? 'justify-center' : 'justify-start'
            } hover:bg-[#FDE6E3] hover:text-[#B1341F]`}
            title={isCollapsed ? 'Uitloggen' : undefined}
            aria-label={isCollapsed ? 'Uitloggen' : undefined}
          >
            <LogOut size={16} />
            {!isCollapsed && <span>Uitloggen</span>}
          </button>
        </div>
      )}
      </>
    );
  };

  return (
    <>
      {/* Mobile Hamburger Menu - only visible on mobile */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40 px-4 py-3 flex items-center justify-between">
        <Logo variant="black" size="sm" />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Menu openen"
            >
              <Menu size={24} style={{ color: '#121315' }} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-white">
            <div className="h-full flex flex-col">
              <SidebarContent isCollapsed={false} />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar - hidden on mobile, visible on md+ */}
      <div
        className="hidden md:flex bg-white h-screen flex-col relative transition-all duration-300"
        style={{
          width: desktopCollapsed ? '72px' : '256px',
          borderRight: '1px solid #E5E7EB',
        }}
      >
        <SidebarContent isCollapsed={desktopCollapsed} />

        {/* Toggle button - bottom of sidebar */}
        <div className={desktopCollapsed ? "p-2" : "px-3 pb-3"}>
          <button
            onClick={toggleDesktopCollapsed}
            className={desktopCollapsed
              ? "w-full flex items-center justify-center py-3 rounded-lg transition-all"
              : "w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg transition-all text-sm font-medium"
            }
            title={desktopCollapsed ? 'Menu uitklappen' : 'Menu inklappen'}
            aria-label={desktopCollapsed ? 'Menu uitklappen' : 'Menu inklappen'}
            style={{
              minHeight: '44px',
              backgroundColor: '#FCEDEE',
              color: '#E62E73',
              fontWeight: '500',
              border: '1px solid #F3B2BE',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F8D5DE';
              e.currentTarget.style.color = '#B72B5A';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FCEDEE';
              e.currentTarget.style.color = '#E62E73';
            }}
          >
            {desktopCollapsed ? (
              <PanelLeftOpen size={20} />
            ) : (
              <>
                <PanelLeftClose size={16} />
                <span>Inklappen</span>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
